// Staff subdomain router (staff.firstcitypoly.edu.ng)
import { createRouter, createWebHistory } from "vue-router";
import { createLogger } from "@/utils/logger";
import store from "../store";

// Staff Modules
import StudentsMgt from "@/components/modules/StudentsMgt.vue";
import ApplicationsMgt from "@/components/modules/ApplicationsMgt.vue";
import StaffMgt from "@/components/modules/StaffMgt.vue";
import PaymentsMgt from "@/components/modules/PaymentsMgt.vue";
import AcademicsMgt from "@/components/modules/AcademicsMgt.vue";
import NotificationsMgt from "@/components/modules/NotificationsMgt.vue";
import CareersMgt from "@/components/modules/CareersMgt.vue";
import ScholarshipMgt from "@/components/modules/ScholarshipMgt.vue";
import ExamMgt from "@/components/modules/ExamMgt.vue";
import DatabaseMgt from "@/components/modules/DatabaseMgt.vue";
import UtilitiesMgt from "@/components/modules/UtilitiesMgt.vue";
import AdmissionPartnersMgt from "@/components/modules/AdmissionPartnersMgt.vue";

// Staff Views
import StaffLogin from "@/views/staff/StaffLoginView.vue";
import StaffDashboard from "@/views/staff/StaffDashboard.vue";
import StaffSettings from "@/views/staff/StaffSettings.vue";
import StaffReports from "@/views/staff/StaffReports.vue";

// Layouts
import StaffLayout from "@/layouts/StaffLayout.vue";

const logger = createLogger("StaffRouter");

const routes = [
  {
    path: "/",
    name: "staff",
    component: StaffLayout,
    meta: { hideHeader: true, hideFooter: true },
    children: [
      {
        path: "",
        name: "staffLogin",
        component: StaffLogin,
        meta: {
          title: "Staff Login",
          guest: true,
          hideStaffHeader: true,
          hideStaffSidebar: true,
        },
      },
      {
        path: "dashboard",
        name: "staffDashboard",
        component: StaffDashboard,
        meta: { title: "Staff Dashboard", requiresAuth: true },
      },
      {
        path: "database",
        name: "databaseMgt",
        component: DatabaseMgt,
        meta: {
          title: "Database Management",
          requiresAuth: true,
          module: "database",
          permission: "view",
        },
      },
      {
        path: "utilities",
        name: "utilitiesMgt",
        component: UtilitiesMgt,
        meta: {
          title: "Utilities",
          requiresAuth: true,
          module: "utilities",
          permission: "view",
        },
      },
      {
        path: "settings",
        name: "staffSettings",
        component: StaffSettings,
        meta: { title: "Staff Settings", requiresAuth: true },
      },
      {
        path: "reports",
        name: "staffReports",
        component: StaffReports,
        meta: { title: "Reports", requiresAuth: true },
      },
      {
        path: "scholarship",
        name: "scholarshipMgt",
        component: ScholarshipMgt,
        meta: {
          title: "Scholarship Management",
          requiresAuth: true,
          module: "scholarship",
          permission: "view",
        },
      },
      {
        path: "staff",
        name: "staffMgt",
        component: StaffMgt,
        meta: {
          title: "Staff Management",
          requiresAuth: true,
          module: "staff",
          permission: "view",
        },
      },
      {
        path: "applications",
        name: "applicationsMgt",
        component: ApplicationsMgt,
        meta: {
          title: "Applications Management",
          requiresAuth: true,
          module: "applications",
          permission: "view",
        },
      },
      {
        path: "admission-partners",
        name: "admissionPartnersMgt",
        component: AdmissionPartnersMgt,
        meta: {
          title: "Admission Partners",
          requiresAuth: true,
          module: "admissionPartners",
          permission: "view",
        },
      },
      {
        path: "students",
        name: "studentsMgt",
        component: StudentsMgt,
        meta: {
          title: "Students Management",
          requiresAuth: true,
          module: "students",
          permission: "view",
        },
      },
      {
        path: "notifications",
        name: "notificationsMgt",
        component: NotificationsMgt,
        meta: {
          title: "Notifications Management",
          requiresAuth: true,
          module: "notifications",
          permission: "view",
        },
      },
      {
        path: "careers",
        name: "careersMgt",
        component: CareersMgt,
        meta: {
          title: "Careers Management",
          requiresAuth: true,
          module: "careers",
          permission: "view",
        },
      },
      {
        path: "payments",
        name: "paymentsMgt",
        component: PaymentsMgt,
        meta: {
          title: "Payments Management",
          requiresAuth: true,
          module: "payments",
          permission: "view",
        },
      },
      {
        path: "academics",
        name: "academicsMgt",
        component: AcademicsMgt,
        meta: {
          title: "Academics Management",
          requiresAuth: true,
          module: "academics",
          permission: "view",
        },
      },
      {
        path: "exams",
        name: "examMgt",
        component: ExamMgt,
        meta: {
          title: "Exam Management",
          requiresAuth: true,
          module: "exams",
          permission: "view",
        },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory("/"),
  routes,
  scrollBehavior(to) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: "smooth",
      };
    } else {
      return { top: 0 };
    }
  },
});

// Navigation Guard for Authentication Handling
router.beforeEach(async (to, from, next) => {
  store.commit("setLoading", true);

  const isGuestRoute = to.meta.guest === true;

  // Check auth status once per page load (always check to know if user is logged in)
  // This is needed even for guest routes to redirect logged-in users away from login
  if (!store.state.authChecked) {
    try {
      await store.dispatch("checkAuthStatus");
    } catch (error) {
      // 401 errors are expected when not logged in - already handled silently by interceptors
      logger.debug("Auth check failed (expected if not logged in):", error);
      store.commit("setAuthChecked", true);
    }
  }

  const isStaffLoggedIn = store.getters.isStaffAuthenticated;

  // Guest routes (login): allow if not logged in, redirect to dashboard if logged in
  if (isGuestRoute) {
    if (isStaffLoggedIn) {
      store.commit("setLoading", false);
      return next({ name: "staffDashboard" });
    }
    store.commit("setLoading", false);
    return next();
  }

  // Protected routes: require staff auth
  if (!isStaffLoggedIn) {
    store.commit("setLoading", false);
    return next({ name: "staffLogin" });
  }

  // Module permission gate: enforce module+permission meta on protected routes
  if (to.meta.module) {
    const requiredPermission = to.meta.permission || "view";
    if (!store.getters.hasPermission(to.meta.module, requiredPermission)) {
      store.commit("setLoading", false);
      return next({ name: "staffDashboard" });
    }
  }

  store.commit("setLoading", false);
  next();
});
router.afterEach(() => {
  store.commit("setLoading", false);
});

export default router;
